import { FC } from "react";

import { Box, Stack, Theme, SxProps, useTheme } from "@mui/material";
import { FormCategory } from "@simplyk/common";

import { mergeSx } from "../../../../../../common/helpers/mergeSx";
import { useFormV2Context } from "../../../contexts/FormV2Context";

import {
  ORGANIZATION_PAGE_LINK_HOVER_SELECTOR,
  useOrganizationPageLinkProps,
} from "@/canvas/components/OrganizationLogoAndName/helpers/useOrganizationPageLinkProps";
import { VerifiedOrganizationBadge } from "@/canvas/components/VerifiedOrganizationBadge/VerifiedOrganizationBadge";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { useTranslate } from "@/common/hooks/useTranslate";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import Image from "@/components/Image/Image";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { ExternalLink } from "@/icons/outlined";

export type OrganizationHostedByProps = {
  logoSrc: string | null;
  organizationName: string;
  "data-test"?: string;
  sx?: SxProps<Theme>;
};

export const OrganizationHostedBy: FC<OrganizationHostedByProps> = ({
  logoSrc,

  organizationName,
  "data-test": dataTest,
  sx,
}) => {
  const theme = useTheme();
  const { t } = useTranslate();
  const { formCategory, publishedOrgPageUrl, formObject } = useFormV2Context();
  const { isSmallScreen } = useMediaQuery();
  const organizationPageLinkProps = useOrganizationPageLinkProps(publishedOrgPageUrl);

  const imageStyles = {
    width: "60px",
    height: "60px",
    borderRadius: theme.radius(0.5),
    overflow: "hidden",
    flexShrink: 0,
  };
  const title = (() => {
    switch (formCategory) {
      case FormCategory.Event:
      case FormCategory.Custom:
      case FormCategory.Auction:
      case FormCategory.Lottery:
      case FormCategory.Sweepstakes:
        return t("ticketing", "hostedBy.eventTitle");
      case FormCategory.Shop:
      case FormCategory.MembershipV2:
        return t("ticketing", "offeredBy.shopTitle");
      default:
        return t("ticketing", "organizedBy.donationTitle");
    }
  })();

  return (
    <Stack
      direction="row"
      gap={1.5}
      alignItems="center"
      minWidth={0}
      maxWidth="100%"
      data-test={dataTest}
      {...organizationPageLinkProps}
      sx={mergeSx({ textDecoration: "none" }, sx)}
    >
      {logoSrc && (
        <Box sx={imageStyles}>
          <Image
            src={logoSrc}
            alt={organizationName}
            width={60}
            height={60}
            nextImage
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </Box>
      )}
      <Stack direction="column" flexGrow={1} minWidth={0}>
        <FormTypography
          variant="body2"
          vibe="quiet"
          sx={(theme: Theme) => ({ width: "100%", ...theme.mixins.truncateOverflow })}
        >
          {title}
        </FormTypography>

        <Stack direction="row" alignItems="center" gap={1.5} maxWidth="100%">
          <FormTypography
            variant="subtitle1"
            vibe="intense"
            sx={(theme: Theme) => ({
              wordBreak: "break-word",
              [ORGANIZATION_PAGE_LINK_HOVER_SELECTOR]: {
                textDecoration: isSmallScreen ? "none" : "underline",
                textDecorationColor: theme.palette.forms.text.intense,
              },
            })}
          >
            {organizationName}
          </FormTypography>
          {publishedOrgPageUrl && (
            <FormIcon
              size="small"
              sx={(theme: Theme) => ({
                flexShrink: 0,
                color: isSmallScreen ? theme.palette.forms.text.supershy : theme.palette.forms.text.quiet,
              })}
            >
              <ExternalLink />
            </FormIcon>
          )}
        </Stack>
        {formObject.verifiedBadgeVariant === "badge" && <VerifiedOrganizationBadge sx={{ marginTop: 0.25 }} />}
      </Stack>
    </Stack>
  );
};
